// ── SettingsPage — 사용자 설정 + 회원 탈퇴 ──
// 개인정보보호법 38조: 정보주체 삭제 권리 보장
// 회원 탈퇴 시 Firestore의 모든 컬렉션 + Storage 폴더 일괄 삭제

import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, setDoc, query, where } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { deleteUser, signOut } from 'firebase/auth';
import { auth } from '../auth';
import { db } from '../db';
import { storage } from '../storage';
import { PrivacyPolicyModal, SecurityPolicyModal } from './LegalModals';
import { useLang, LANGS } from '../i18n/LangContext';
import { COUNTRIES, useStoreCountry } from '../storeLocale';   // V108 매장 국가
import ShareSemfit from './ShareSemfit';
import VersionCheck from './VersionCheck';   // V146 셈핏 버전 · 새 버전 확인
import { fingerprintFromTx, fingerprintFromPurchaseLog } from '../utils/receiptFingerprint';
import { exportFullBackup } from '../utils/backupExport';
import { parseBackupExcel, summarizeParsed, restoreFromBackup } from '../utils/backupImport';
import { createSnapshot, listSnapshots, deleteSnapshot } from '../utils/snapshots';
import { inviteManager, listManagers, removeManager, updateManagerPermissions, listInvitations, approveManager, rejectManager, cancelInvitation } from '../utils/managers';
import {
  PERMISSION_CATEGORIES, PERMISSION_PRESETS, PERMISSION_LEVELS,
  LEVEL_LABEL, LEVEL_COLOR, DEFAULT_PERMISSIONS,
  normalizePermissions, detectPreset,
} from '../utils/permissions';

export default function SettingsPage({ user, canAccess, isManagerMode, currentStore, onSignOut, backupData, restoreWriters }) {
  const { lang, setLang } = useLang();          // V99 표시 언어 (한/중)
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V147 직원(매니저)이 보는 칸은 중국어로
  const { country } = useStoreCountry(user?.uid);   // V108 매장 국가
  const [savingCountry, setSavingCountry] = useState(false);
  // 매장 국가는 owner만 바꾼다. 매니저가 남의 매장 통화를 바꾸면 안 된다.
  const changeCountry = async (next) => {
    if (!user?.uid || next === country) return;
    const meta = COUNTRIES.find(c => c.id === next);
    if (!window.confirm(
      `매장 국가를 ${meta?.label}(으)로 바꿉니다.\n\n` +
      `금액 표시가 ${meta?.symbol} 기준으로 바뀌고, 영수증 사진 인식도 ${meta?.label} 영수증에 맞춰집니다.\n` +
      `이미 입력된 숫자는 바뀌지 않습니다.\n\n계속할까요?`
    )) return;
    setSavingCountry(true);
    try { await setDoc(doc(db, 'users', user.uid), { country: next }, { merge: true }); }
    catch (e) { alert('저장 실패: ' + e.message); }
    finally { setSavingCountry(false); }
  };
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [step, setStep] = useState(0);  // 0: 초기, 1: 확인 모달
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [progress, setProgress] = useState('');

  const expectedConfirm = '셈핏 탈퇴';
  const canDelete = confirmText === expectedConfirm && !deleting;

  // POS 가져오기 데이터 일괄 삭제 (중복 정리용)
  const [posDeleting, setPosDeleting] = useState(false);
  const [posResult, setPosResult] = useState('');
  const handleDeletePosImport = async () => {
    if (!user?.uid) return;
    if (!window.confirm('POS 엑셀 가져오기로 등록된 모든 매출을 삭제합니다.\n(거래장부의 source=pos-import 거래만 삭제. 직접 입력한 매출은 유지)\n\n자동으로 스냅샷이 만들어집니다.\n계속할까요?')) return;
    setPosDeleting(true);
    setPosResult('🔄 스냅샷 만드는 중...');
    try {
      try {
        await createSnapshot(user.uid, {
          label: 'POS 임포트 전체 삭제 전 백업',
          reason: 'POS-delete-all',
          scopes: ['transactions'],
          filter: { field: 'source', op: '==', value: 'pos-import' },
        });
      } catch (e) { console.warn('snapshot failed:', e.message); }
      const q = query(
        collection(db, 'users', user.uid, 'transactions'),
        where('source', '==', 'pos-import')
      );
      const snap = await getDocs(q);
      let deleted = 0, failed = 0;
      for (const d of snap.docs) {
        try { await deleteDoc(d.ref); deleted++; }
        catch (e) { failed++; console.warn('POS tx delete failed:', e.message); }
      }
      setPosResult(`✅ ${deleted}건 삭제 완료${failed > 0 ? ` (실패 ${failed}건)` : ''}`);
    } catch (e) {
      setPosResult(`❌ 삭제 실패: ${e.message}`);
    } finally {
      setPosDeleting(false);
    }
  };

  // ── OCR 영수증 중복 정리 (fingerprint 기준 그룹핑 후 첫 1건만 남기고 삭제) ──
  const [dupAnalyzing, setDupAnalyzing] = useState(false);
  const [dupResult, setDupResult] = useState('');
  const handleCleanupOcrDuplicates = async () => {
    if (!user?.uid) return;
    setDupAnalyzing(true);
    setDupResult('🔍 분석 중...');
    try {
      // 1) transactions 스캔 — fingerprint 그룹화
      const txSnap = await getDocs(collection(db, 'users', user.uid, 'transactions'));
      const txGroups = new Map();
      for (const d of txSnap.docs) {
        const fp = fingerprintFromTx(d.data());
        if (!fp) continue;
        if (!txGroups.has(fp)) txGroups.set(fp, []);
        txGroups.get(fp).push({ ref: d.ref, id: d.id, data: d.data() });
      }
      // 2) purchaseLog 스캔 — fingerprint 그룹화
      const logSnap = await getDocs(collection(db, 'users', user.uid, 'purchaseLog'));
      const logGroups = new Map();
      for (const d of logSnap.docs) {
        const fp = fingerprintFromPurchaseLog(d.data());
        if (!fp) continue;
        if (!logGroups.has(fp)) logGroups.set(fp, []);
        logGroups.get(fp).push({ ref: d.ref, id: d.id, data: d.data() });
      }
      // 3) 중복 그룹 (size > 1)
      const txDupGroups = Array.from(txGroups.values()).filter(g => g.length > 1);
      const logDupGroups = Array.from(logGroups.values()).filter(g => g.length > 1);
      const txDelCount = txDupGroups.reduce((s, g) => s + g.length - 1, 0);
      const logDelCount = logDupGroups.reduce((s, g) => s + g.length - 1, 0);

      if (txDelCount + logDelCount === 0) {
        setDupResult('✅ 중복 영수증 없음 (깔끔)');
        return;
      }
      // 4) 미리보기 + 사용자 확인
      const preview = [
        ...txDupGroups.slice(0, 3).map(g => {
          const d = g[0].data;
          return `  · ${d.date || '?'} ${d.partnerName || '?'} ${Math.round((Number(d.supply) || 0) + (Number(d.vat) || 0)).toLocaleString()}원 → ${g.length}건 중복`;
        }),
        ...logDupGroups.slice(0, 2).map(g => {
          const d = g[0].data;
          return `  · ${d.date || '?'} ${d.partnerName || '?'} ${Math.round(Number(d.total) || 0).toLocaleString()}원 (메모) → ${g.length}건 중복`;
        }),
      ].join('\n');
      const proceed = window.confirm(
        `🔍 중복 영수증 분석 결과\n\n` +
        `· 거래장부: ${txDupGroups.length}개 그룹 → ${txDelCount}건 삭제 예정\n` +
        `· 메모 영수증: ${logDupGroups.length}개 그룹 → ${logDelCount}건 삭제 예정\n\n` +
        `예시:\n${preview}\n\n` +
        `각 영수증의 첫 1건만 남기고 나머지 ${txDelCount + logDelCount}건을 삭제합니다.\n` +
        `이 작업은 되돌릴 수 없어요. 계속할까요?`
      );
      if (!proceed) {
        setDupResult(`분석 완료 (취소됨): 거래장부 ${txDelCount}건 + 메모 ${logDelCount}건 중복`);
        return;
      }
      try {
        await createSnapshot(user.uid, {
          label: 'OCR 영수증 중복 정리 전 백업',
          reason: 'OCR-dedupe',
          scopes: ['transactions', 'purchaseLog'],
        });
      } catch (e) { console.warn('snapshot failed:', e.message); }
      // 5) 삭제 — doc.id 정렬(Firestore auto-id는 시간순) → 첫 번째 keep
      let deleted = 0, failed = 0;
      for (const group of [...txDupGroups, ...logDupGroups]) {
        group.sort((a, b) => a.id.localeCompare(b.id));
        const toDelete = group.slice(1);
        for (const item of toDelete) {
          try { await deleteDoc(item.ref); deleted++; }
          catch (e) { failed++; console.warn('dup delete failed:', e.message); }
        }
      }
      setDupResult(`✅ ${deleted}건 삭제 완료${failed > 0 ? ` (실패 ${failed}건)` : ''} · 새로고침하면 반영돼요`);
    } catch (e) {
      setDupResult(`❌ 실패: ${e.message}`);
    } finally {
      setDupAnalyzing(false);
    }
  };

  // ── POS 매출 중복 정리 (date + category.sub + amount 기준 — 수기 입력은 안 건드림) ──
  const [posDedupAnalyzing, setPosDedupAnalyzing] = useState(false);
  const [posDedupResult, setPosDedupResult] = useState('');
  const handleCleanupPosDuplicates = async () => {
    if (!user?.uid) return;
    setPosDedupAnalyzing(true);
    setPosDedupResult('🔍 분석 중...');
    try {
      const q = query(
        collection(db, 'users', user.uid, 'transactions'),
        where('source', '==', 'pos-import')
      );
      const snap = await getDocs(q);
      const groups = new Map();
      for (const d of snap.docs) {
        const data = d.data();
        const date = String(data.date || '').slice(0, 10);
        const sub = String(data?.category?.sub || '').toLowerCase();
        const amount = Math.round((Number(data.supply) || 0) + (Number(data.vat) || 0));
        if (!date || amount <= 0) continue;
        const fp = `${date}|${sub}|${amount}`;
        if (!groups.has(fp)) groups.set(fp, []);
        groups.get(fp).push({ ref: d.ref, id: d.id, data });
      }
      const dupGroups = Array.from(groups.values()).filter(g => g.length > 1);
      const delCount = dupGroups.reduce((s, g) => s + g.length - 1, 0);
      if (delCount === 0) {
        setPosDedupResult('✅ POS 매출 중복 없음');
        return;
      }
      const preview = dupGroups.slice(0, 5).map(g => {
        const d = g[0].data;
        const subLabel = d?.category?.sub === 'sales_card' ? '카드'
          : d?.category?.sub === 'sales_cash' ? '현금'
          : d?.category?.sub === 'sales_delivery' ? '배달' : (d?.category?.sub || '?');
        return `  · ${d.date} ${subLabel} ${Math.round((Number(d.supply) || 0) + (Number(d.vat) || 0)).toLocaleString()}원 → ${g.length}건 중복`;
      }).join('\n');
      const more = dupGroups.length > 5 ? `\n  ... 외 ${dupGroups.length - 5}건` : '';
      const proceed = window.confirm(
        `🔍 POS 매출 중복 분석\n\n` +
        `${dupGroups.length}개 그룹 → ${delCount}건 삭제 예정\n\n` +
        `예시:\n${preview}${more}\n\n` +
        `각 그룹의 첫 1건만 남기고 ${delCount}건 삭제합니다.\n` +
        `※ 수기로 직접 입력한 매출은 건드리지 않아요 (source=pos-import만 검사).\n` +
        `이 작업은 되돌릴 수 없어요. 계속할까요?`
      );
      if (!proceed) {
        setPosDedupResult(`분석 완료 (취소됨): ${delCount}건 중복`);
        return;
      }
      try {
        await createSnapshot(user.uid, {
          label: 'POS 매출 중복 정리 전 백업',
          reason: 'POS-dedupe',
          scopes: ['transactions'],
          filter: { field: 'source', op: '==', value: 'pos-import' },
        });
      } catch (e) { console.warn('snapshot failed:', e.message); }
      let deleted = 0, failed = 0;
      for (const group of dupGroups) {
        group.sort((a, b) => a.id.localeCompare(b.id));
        const toDelete = group.slice(1);
        for (const item of toDelete) {
          try { await deleteDoc(item.ref); deleted++; }
          catch (e) { failed++; console.warn('POS dedup delete failed:', e.message); }
        }
      }
      setPosDedupResult(`✅ ${deleted}건 삭제 완료${failed > 0 ? ` (실패 ${failed}건)` : ''} · 새로고침하면 반영돼요`);
    } catch (e) {
      setPosDedupResult(`❌ 실패: ${e.message}`);
    } finally {
      setPosDedupAnalyzing(false);
    }
  };

  // ── 전체 백업 다운로드 ──
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupResult, setBackupResult] = useState('');
  const handleFullBackup = async () => {
    if (!user?.uid) return;
    setBackupBusy(true);
    setBackupResult('🔄 준비 중...');
    try {
      const result = await exportFullBackup(user.uid, backupData, (msg) => setBackupResult(`🔄 ${msg}`));
      const { counts } = result;
      setBackupResult(
        `✅ 다운로드 완료 — ${result.fileName}\n` +
        `· 거래장부 ${counts.transactions}건 · 단가표 ${counts.prices}개 · 레시피 ${counts.recipes}개\n` +
        `· 직원 ${counts.employees}명 · 매입메모 ${counts.purchaseLog}건 · 발주 ${counts.orderHistory}건`
      );
    } catch (e) {
      setBackupResult(`❌ 실패: ${e.message}`);
    } finally {
      setBackupBusy(false);
    }
  };

  // ── 백업 엑셀 → Firestore 복원 ──
  const [restoreParsed, setRestoreParsed] = useState(null);
  const [restoreFileName, setRestoreFileName] = useState('');
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreResult, setRestoreResult] = useState('');
  const [restoreConfirmText, setRestoreConfirmText] = useState('');

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setRestoreResult('🔄 파일 분석 중...');
    setRestoreParsed(null);
    setRestoreConfirmText('');
    try {
      const parsed = await parseBackupExcel(file);
      setRestoreParsed(parsed);
      setRestoreFileName(file.name);
      setRestoreResult('');
    } catch (err) {
      setRestoreResult(`❌ 파일 읽기 실패: ${err.message}`);
    }
  };

  const handleConfirmRestore = async () => {
    if (!user?.uid || !restoreParsed) return;
    if (restoreConfirmText !== '복원') {
      alert('"복원"을 정확히 입력해야 진행됩니다.');
      return;
    }
    if (!restoreWriters) {
      alert('writer 함수가 없습니다 (App.js props 확인 필요)');
      return;
    }
    setRestoreBusy(true);
    setRestoreResult('🔄 스냅샷 만드는 중...');
    try {
      try {
        await createSnapshot(user.uid, {
          label: '엑셀 복원 직전 백업',
          reason: 'backup-restore',
          scopes: ['transactions', 'purchaseLog'],
        });
      } catch (snapErr) { console.warn('snapshot failed:', snapErr.message); }

      const result = await restoreFromBackup(
        user.uid, restoreParsed, restoreWriters,
        { mode: 'replace', onProgress: (msg) => setRestoreResult(`🔄 ${msg}`) }
      );

      const r = result.restored;
      const lines = [
        '✅ 복원 완료 — 새로고침하면 반영돼요',
        `· 거래장부 ${r.transactions || 0}건 · 매입메모 ${r.purchaseLog || 0}건`,
        `· 단가표 ${r.prices || 0}개 · 레시피 ${r.categories || 0}개 · 직원 ${r.employees || 0}명`,
        `· 손익수기 ${r.profitLoss || 0}개월 · 발주 ${r.orderHistory || 0}건`,
      ];
      if (result.errors.length > 0) {
        lines.push(`⚠️ 오류 ${result.errors.length}건 (콘솔 확인)`);
        result.errors.forEach(e => console.warn('[restore]', e));
      }
      setRestoreResult(lines.join('\n'));
      setRestoreParsed(null);
      setRestoreConfirmText('');
    } catch (e) {
      setRestoreResult(`❌ 복원 실패: ${e.message}`);
    } finally {
      setRestoreBusy(false);
    }
  };

  const handleCancelRestore = () => {
    setRestoreParsed(null);
    setRestoreConfirmText('');
    setRestoreResult('');
  };


  // ── 스냅샷 목록 (위험 작업 전 자동 백업 이력) ──
  const [snapshots, setSnapshots] = useState([]);
  const [snapLoading, setSnapLoading] = useState(false);
  const loadSnapshots = async () => {
    if (!user?.uid) return;
    setSnapLoading(true);
    try {
      const list = await listSnapshots(user.uid, 20);
      setSnapshots(list);
    } catch (e) { console.warn('snapshot list failed:', e.message); }
    finally { setSnapLoading(false); }
  };
  useEffect(() => { loadSnapshots(); /* eslint-disable-line */ }, [user?.uid]);
  const handleDeleteSnapshot = async (id) => {
    if (!window.confirm('이 스냅샷을 삭제할까요?\n복원 데이터가 영구히 사라집니다.')) return;
    try {
      await deleteSnapshot(user.uid, id);
      await loadSnapshots();
    } catch (e) { alert('삭제 실패: ' + e.message); }
  };

  // ── 매니저 초대 (V49/V50) — Pro+ 전용 ──
  const isPro = typeof canAccess === 'function' ? canAccess('pro') : false;
  const [managers, setManagers] = useState([]);
  const [invitations, setInvitations] = useState([]);  // V95
  const [managerLoading, setManagerLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePerms, setInvitePerms] = useState(DEFAULT_PERMISSIONS);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState('');
  const [editingMgr, setEditingMgr] = useState(null);  // { uid, email, permissions }
  const [editPerms, setEditPerms] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const loadManagers = async () => {
    if (!user?.uid) return;
    setManagerLoading(true);
    try {
      const [mgrList, invList] = await Promise.all([
        listManagers(user.uid),
        listInvitations(user.uid),
      ]);
      setManagers(mgrList);
      setInvitations(invList);
    } catch (e) {
      console.warn('manager list failed:', e.message);
    } finally {
      setManagerLoading(false);
    }
  };

  // V95: 승인/거부/취소 핸들러
  const handleApproveManager = async (m) => {
    try {
      await approveManager(user.uid, m.uid, m.email);
      await loadManagers();
    } catch (e) { alert('승인 실패: ' + e.message); }
  };
  const handleRejectManager = async (m) => {
    if (!window.confirm(`${m.email || '이 매니저'}의 가입을 거부할까요?\n매니저 등록과 초대장 모두 삭제됩니다.`)) return;
    try {
      await rejectManager(user.uid, m.uid, m.email);
      await loadManagers();
    } catch (e) { alert('거부 실패: ' + e.message); }
  };
  const handleCancelInvitation = async (email) => {
    if (!window.confirm(`${email} 으로 보낸 초대를 취소할까요?`)) return;
    try {
      await cancelInvitation(email);
      await loadManagers();
    } catch (e) { alert('취소 실패: ' + e.message); }
  };
  useEffect(() => { if (isPro) loadManagers(); /* eslint-disable-line */ }, [user?.uid, isPro]);
  const handleInviteManager = async () => {
    if (!user?.uid || !inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteResult('');
    try {
      await inviteManager(user.uid, user.email || '', inviteEmail.trim(), invitePerms);
      setInviteResult(`✅ ${inviteEmail.trim()} 님에게 초대장을 보냈어요. 매니저가 같은 이메일로 로그인하면 자동 연결됩니다.`);
      setInviteEmail('');
      setInvitePerms(DEFAULT_PERMISSIONS);
      await loadManagers();
    } catch (e) {
      setInviteResult(`❌ ${e.message}`);
    } finally {
      setInviteBusy(false);
    }
  };
  const handleRemoveManager = async (managerUid, email) => {
    if (!window.confirm(`매니저 ${email || managerUid}를 해제할까요?\n해제 후 즉시 매장 데이터 접근이 차단됩니다.`)) return;
    try {
      await removeManager(user.uid, managerUid);
      await loadManagers();
    } catch (e) {
      alert('해제 실패: ' + e.message);
    }
  };
  const openEditManager = (m) => {
    setEditingMgr(m);
    setEditPerms(normalizePermissions(m.permissions || DEFAULT_PERMISSIONS));
  };
  const handleSavePerms = async () => {
    if (!editingMgr || !editPerms) return;
    setEditBusy(true);
    try {
      await updateManagerPermissions(user.uid, editingMgr.uid, editPerms);
      setEditingMgr(null);
      setEditPerms(null);
      await loadManagers();
    } catch (e) {
      alert('권한 저장 실패: ' + e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!canDelete || !user?.uid) return;
    setDeleting(true);
    try {
      const uid = user.uid;

      // 1) Firestore — users/{uid} 하위 모든 서브컬렉션 삭제
      const subcollections = [
        'transactions', 'categories', 'prices', 'itemPrices',
        'orderHistory', 'inventory', 'employees', 'schedules',
        'profitLoss', 'recipes', 'purchaseLog', 'taxData',
        'goals', 'menus', 'groupBuyInterest', 'receivings', 'receivingPhotos',   // V158 입고
      ];
      for (const sub of subcollections) {
        setProgress(`거래장부·자료 삭제 중... (${sub})`);
        try {
          const ref = collection(db, 'users', uid, sub);
          const snap = await getDocs(ref);
          for (const d of snap.docs) {
            await deleteDoc(d.ref);
          }
        } catch (e) {
          console.warn(`${sub} 삭제 실패 (계속 진행):`, e.message);
        }
      }

      // 2) Firestore — users/{uid} 본 문서 삭제
      setProgress('계정 정보 삭제 중...');
      try {
        await deleteDoc(doc(db, 'users', uid));
      } catch (e) {
        console.warn('계정 문서 삭제 실패:', e.message);
      }

      // 3) Storage — users/{uid}/* 모든 영수증 사진 삭제
      setProgress('영수증 사진 삭제 중...');
      try {
        const userRef = ref(storage, `users/${uid}`);
        await deleteAllInFolder(userRef);
      } catch (e) {
        console.warn('Storage 삭제 실패 (계속 진행):', e.message);
      }

      // 4) Firebase Auth 사용자 삭제
      setProgress('인증 정보 삭제 중...');
      try {
        if (auth.currentUser) {
          await deleteUser(auth.currentUser);
        }
      } catch (e) {
        // Auth 삭제는 최근 로그인 필요 — 실패 시 로그아웃만
        console.warn('Auth 삭제 실패 (로그아웃 처리):', e.message);
        await signOut(auth);
      }

      alert('✓ 회원 탈퇴 완료. 모든 데이터가 영구 삭제되었습니다.');
      if (onSignOut) onSignOut();
    } catch (e) {
      console.error('탈퇴 실패:', e);
      alert(`탈퇴 처리 중 오류가 발생했습니다: ${e.message}\n\n잠시 후 다시 시도하시거나 u012me@gmail.com으로 문의해주세요.`);
      setDeleting(false);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#FAFAFA', padding: 24 }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 24, color: '#18181B', letterSpacing: '-0.02em' }}>
          {Z('설정', '设置')}
        </h1>

        {/* V146: 지금 보고 있는 셈핏 버전 — 고친 기능이 그대로일 때 먼저 확인 */}
        <Card title={Z('📱 셈핏 버전', '📱 SEMFIT 版本')}>
          <VersionCheck />
        </Card>

        {/* 계정 정보 */}
        <Card title={Z('👤 계정 정보', '👤 账号信息')}>
          <Row label={Z('이메일', '邮箱')} value={user?.email || '—'} />
          <Row label={Z('이름', '姓名')} value={user?.displayName || '—'} />
          <Row label="UID" value={user?.uid?.slice(0, 12) + '...' || '—'} mono />
          {isManagerMode && (
            <div style={{
              marginTop: 10, padding: '10px 12px',
              background: "#FAFAFA", border: '1px solid #F4F4F5', borderRadius: 8,
              fontSize: 12, color: '#9A3412', lineHeight: 1.5,
            }}>
              {lang === 'zh' ? <>
              👥 <strong>管理员模式</strong> — 现在管理的是 <strong>{currentStore?.ownerStoreName || currentStore?.ownerEmail || '其他门店'}</strong> 的门店。
              付款 · 使用券 · 管理员管理 · 注销账号等老板专用菜单已隐藏。
              要回到自己的账号，请在上方门店切换里选 <strong>我的门店</strong>。</> : <>
              👥 <strong>매니저 모드</strong> — 현재 <strong>{currentStore?.ownerEmail || '다른 매장'}</strong>의 매장을 관리 중이에요.
              결제·플랜·매니저 관리·회원 탈퇴 같은 사장 전용 메뉴는 숨겨져 있습니다.
              본인 계정으로 돌아가려면 상단 매장 전환에서 <strong>내 매장</strong>을 선택하세요.</>}
            </div>
          )}
        </Card>

        {/* ── V99 표시 언어 (한중 병기) ── */}
        <Card title={Z('🌐 표시 언어 / 显示语言', '🌐 显示语言 / 표시 언어')}>   {/* 한국어 화면에서도 중국 직원이 찾을 수 있게 병기 유지 */}
          <div style={{ fontSize: 13, color: '#52525B', lineHeight: 1.6, marginBottom: 12 }}>
            {lang === 'zh' ? <>
            选择按钮、菜单等<strong>页面文字</strong>的语言。每个账号分开保存，老板看韩语、员工看中文，看的是同一个门店。
            <div style={{ marginTop: 6, color: '#9A3412' }}>
              单价表的<strong>品名</strong>不管选哪种语言，都会韩中两种一起显示。
            </div></> : <>
            버튼·메뉴 같은 <strong>화면 문구</strong>의 언어를 고릅니다. 계정마다 따로 저장되니
            사장님은 한국어로, 중국 직원은 중국어로 같은 매장을 볼 수 있어요.
            <div style={{ marginTop: 6, color: '#9A3412' }}>
              단가표 <strong>품목명</strong>은 언어 설정과 무관하게 항상 한중 병기로 표시됩니다.
            </div></>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {LANGS.map(L => {
              const active = lang === L.id;
              return (
                <button key={L.id} onClick={() => setLang(L.id)}
                  style={{
                    padding: '10px 20px', borderRadius: 10,
                    border: active ? '2px solid #EA580C' : '1.5px solid #E4E4E7',
                    background: active ? '#FFF7ED' : '#fff',
                    color: active ? '#9A3412' : '#52525B',
                    fontSize: 14, fontWeight: active ? 800 : 600,
                    cursor: 'pointer', minWidth: 120,
                  }}>
                  {L.native}
                  <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2, opacity: 0.75 }}>
                    {lang === 'zh' ? (L.id === 'ko' ? '韩语' : '中文') : L.label}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{
            marginTop: 12, padding: '10px 12px', background: '#FAFAFA',
            border: '1px solid #F4F4F5', borderRadius: 8,
            fontSize: 12, color: '#71717A', lineHeight: 1.5,
          }}>
            {lang === 'zh' ? <>品项的中文名称可以在<strong>单价表 → 中 一键补全中文</strong>里一次补齐。</> : <>
            아직 번역되지 않은 문구는 한국어로 표시됩니다. 품목 중국어명은
            <strong> 단가표 → 中 중국어 일괄</strong> 버튼으로 한 번에 채울 수 있어요.</>}
          </div>
        </Card>

        {/* ── V108 매장 국가 — owner만 ── */}
        {!isManagerMode && (
        <Card title="🌏 매장 국가 / 门店所在国">
          <div style={{ fontSize: 13, color: '#52525B', lineHeight: 1.6, marginBottom: 12 }}>
            이 매장이 어느 나라에 있는지 고릅니다. 세 가지가 함께 바뀝니다 —
            <strong> 금액 단위</strong>, <strong>영수증 사진 인식 방식</strong>,
            그리고 한국 세무 기능의 해당 여부입니다.
            <div style={{ marginTop: 6, color: '#9A3412' }}>
              이미 입력된 숫자는 바뀌지 않습니다. 단위 표시만 바뀝니다.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {COUNTRIES.map(C => {
              const active = country === C.id;
              return (
                <button key={C.id} onClick={() => changeCountry(C.id)} disabled={savingCountry}
                  style={{
                    padding: '10px 20px', borderRadius: 10,
                    border: active ? '2px solid #EA580C' : '1.5px solid #E4E4E7',
                    background: active ? '#FFF7ED' : '#fff',
                    color: active ? '#9A3412' : '#52525B',
                    fontSize: 14, fontWeight: active ? 800 : 600,
                    cursor: savingCountry ? 'default' : 'pointer', minWidth: 130,
                    opacity: savingCountry ? 0.6 : 1,
                  }}>
                  {C.native}
                  <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2, opacity: 0.75 }}>
                    금액 {C.symbol}
                  </div>
                </button>
              );
            })}
          </div>
          {country === 'CN' && (
            <div style={{
              marginTop: 12, padding: '10px 12px', background: '#FAFAFA',
              border: '1px solid #F4F4F5', borderRadius: 8,
              fontSize: 12, color: '#71717A', lineHeight: 1.6,
            }}>
              중국 매장은 영수증을 AI가 사진에서 직접 읽습니다 (收银小票·发票·收据·모바일 결제 캡처).
              한국 세무회계 탭의 부가세·종소세 기능은 중국 매장에는 해당하지 않습니다.
            </div>
          )}
        </Card>
        )}

        {/* 친구 초대 — 매니저 모드에서는 의미 없으니 숨김 */}
        {!isManagerMode && (
          <div style={{ marginBottom: 16 }}>
            <ShareSemfit user={user} />
          </div>
        )}

        {/* 매니저 초대 (V49) — Pro+ 전용, 사장만 */}
        {!isManagerMode && (
        <Card title="👥 매장 매니저 관리">
          {!isPro ? (
            <div>
              <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 10px' }}>
                매니저를 초대하면 직원·아르바이트·동업자가 같은 매장 데이터에 함께 접근할 수 있어요.
                거래 입력·OCR·재고 확인 등 일상 업무를 분담하면서 사장님 권한과 분리됩니다.
              </p>
              <div style={{
                padding: '10px 12px', background: '#F4F4F5',
                border: '1px solid #D4D4D8', borderRadius: 8,
                fontSize: 12, color: '#52525B', lineHeight: 1.6,
              }}>
                🔒 <strong>프로 플랜 이상</strong>에서 사용 가능한 기능이에요.
                상단 <strong>플랜 업그레이드</strong>에서 전환할 수 있어요.
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
                매니저별로 <strong>카테고리 9개</strong>의 권한을 따로 설정할 수 있어요.
                직원·세무 같은 민감 정보는 <strong>'없음'</strong>으로 막아두는 걸 권장합니다.
                삭제는 어떤 권한이든 사장님만 가능해요.
              </p>

              {/* 초대 입력 */}
              <div style={{
                padding: 12, background: '#f9fafb',
                border: '1px solid #e5e7eb', borderRadius: 8,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                  ➕ 새 매니저 초대
                </div>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="manager@example.com"
                  disabled={inviteBusy}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: '1px solid #d1d5db', borderRadius: 6,
                    fontSize: 13, boxSizing: 'border-box', marginBottom: 10,
                  }} />

                <PermissionEditor value={invitePerms} onChange={setInvitePerms} />

                <button
                  onClick={handleInviteManager}
                  disabled={inviteBusy || !inviteEmail.trim()}
                  style={{
                    width: '100%', padding: '11px 16px',
                    borderRadius: 6, border: 'none',
                    background: (inviteBusy || !inviteEmail.trim())
                      ? '#9ca3af' : '#EA580C',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: (inviteBusy || !inviteEmail.trim()) ? 'not-allowed' : 'pointer',
                    marginTop: 10,
                  }}>
                  {inviteBusy ? '초대 중...' : '초대장 보내기'}
                </button>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
                  ℹ 매니저가 같은 이메일로 셈핏에 <strong>처음 로그인</strong>하면 자동으로 연결됩니다.
                </div>
                {inviteResult && (
                  <div style={{
                    marginTop: 10, fontSize: 12,
                    color: inviteResult.startsWith('✅') ? '#9A3412' : '#52525B',
                    background: inviteResult.startsWith('✅') ? '#FAFAFA' : '#fef2f2',
                    padding: '8px 10px', borderRadius: 6, lineHeight: 1.5,
                  }}>
                    {inviteResult}
                  </div>
                )}
              </div>

              {/* V95: 3섹션 매니저 관리 — 초대 대기 / 승인 대기 / 활성 */}
              {managerLoading ? (
                <div style={{ fontSize: 12, color: '#9ca3af' }}>불러오는 중...</div>
              ) : (() => {
                // 활성/비활성 분리
                const activeManagers  = managers.filter(m => m.isActive !== false);
                const pendingApproval = managers.filter(m => m.isActive === false);
                // 가입 안 한 초대 = invitations 중 매니저 doc이 없는 것
                const acceptedEmails = new Set(managers.map(m => (m.email || '').toLowerCase()));
                const pendingInvites = invitations.filter(inv => !acceptedEmails.has((inv.email || '').toLowerCase()));

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                    {/* ① 초대 대기 (가입 안 함) */}
                    {pendingInvites.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#52525B', marginBottom: 6 }}>
                          ① 초대 대기 — 가입 안 함 ({pendingInvites.length}명)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {pendingInvites.map(inv => (
                            <div key={inv.email} style={{
                              padding: '10px 12px', background: '#FAFAFA',
                              border: '1px dashed #A1A1AA', borderRadius: 6, fontSize: 12,
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between',
                                             alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 2 }}>
                                    {inv.email}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#71717A' }}>
                                    초대장 보냄 · 매니저가 같은 이메일로 가입하면 다음 단계
                                  </div>
                                </div>
                                <button onClick={() => handleCancelInvitation(inv.email)}
                                  style={{
                                    padding: '6px 10px', borderRadius: 4,
                                    border: '1px solid #D4D4D8', background: '#fff',
                                    color: '#52525B', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  }}>
                                  초대 취소
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ② 가입 완료, 사장 승인 대기 */}
                    {pendingApproval.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#92400E', marginBottom: 6 }}>
                          ② 승인 대기 — 매니저 가입 완료, 사장 승인 필요 ({pendingApproval.length}명)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {pendingApproval.map(m => {
                            const presetKey = detectPreset(m.permissions);
                            const presetLabel = presetKey === 'custom'
                              ? '커스텀'
                              : (PERMISSION_PRESETS[presetKey]?.icon + ' ' + PERMISSION_PRESETS[presetKey]?.label);
                            return (
                              <div key={m.uid} style={{
                                padding: '10px 12px', background: '#FFFBEB',
                                border: '1.5px solid #FCD34D', borderRadius: 6, fontSize: 12,
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between',
                                               alignItems: 'center', gap: 8 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: '#18181B', marginBottom: 2 }}>
                                      {m.email || '(이메일 없음)'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#71717A' }}>
                                      권한: <strong>{presetLabel}</strong> · 승인하면 즉시 매장 접근 가능
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <button onClick={() => handleApproveManager(m)}
                                      style={{
                                        padding: '6px 12px', borderRadius: 4, border: 'none',
                                        background: '#18181B', color: '#fff',
                                        fontSize: 11, fontWeight: 800, cursor: 'pointer',
                                      }}>
                                      ✓ 승인
                                    </button>
                                    <button onClick={() => handleRejectManager(m)}
                                      style={{
                                        padding: '6px 10px', borderRadius: 4,
                                        border: '1px solid #fca5a5', background: '#fff',
                                        color: '#52525B', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                      }}>
                                      거부
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ③ 활성 매니저 */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#18181B', marginBottom: 6 }}>
                        ③ 활성 매니저 ({activeManagers.length}명)
                      </div>
                      {activeManagers.length === 0 ? (
                        <div style={{
                          fontSize: 12, color: '#9ca3af', fontStyle: 'italic',
                          padding: '12px', background: '#f9fafb', borderRadius: 6, textAlign: 'center',
                        }}>
                          아직 활성 매니저가 없어요
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {activeManagers.map(m => {
                            const presetKey = detectPreset(m.permissions);
                            const presetLabel = presetKey === 'custom'
                              ? '커스텀'
                              : (PERMISSION_PRESETS[presetKey]?.icon + ' ' + PERMISSION_PRESETS[presetKey]?.label);
                            return (
                              <div key={m.uid} style={{
                                padding: '10px 12px', background: '#fff',
                                border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12,
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between',
                                               alignItems: 'center', gap: 8 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: 2 }}>
                                      {m.email || '(이메일 없음)'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                                      권한: <strong>{presetLabel}</strong>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <button onClick={() => openEditManager(m)}
                                      style={{
                                        padding: '6px 10px', borderRadius: 4,
                                        border: '1px solid #FF6F0F', background: '#fff',
                                        color: '#18181B', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                      }}>
                                      권한 수정
                                    </button>
                                    <button onClick={() => handleRemoveManager(m.uid, m.email)}
                                      style={{
                                        padding: '6px 10px', borderRadius: 4,
                                        border: '1px solid #fca5a5', background: '#fff',
                                        color: '#52525B', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                      }}>
                                      해제
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* 합계 0인 경우 */}
                    {pendingInvites.length === 0 && pendingApproval.length === 0 && activeManagers.length === 0 && (
                      <div style={{
                        fontSize: 12, color: '#9ca3af', fontStyle: 'italic',
                        padding: '12px', background: '#f9fafb', borderRadius: 6, textAlign: 'center',
                      }}>
                        아직 등록된 매니저가 없어요. 위에서 초대를 시작하세요.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 권한 수정 모달 */}
              {editingMgr && editPerms && (
                <div style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 9999, padding: 16,
                }} onClick={() => !editBusy && setEditingMgr(null)}>
                  <div style={{
                    background: '#fff', borderRadius: 12, padding: 20,
                    maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto',
                  }} onClick={e => e.stopPropagation()}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>
                      매니저 권한 수정
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
                      {editingMgr.email}
                    </div>
                    <PermissionEditor value={editPerms} onChange={setEditPerms} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <button onClick={() => setEditingMgr(null)} disabled={editBusy}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 6,
                          border: '1px solid #d1d5db', background: '#fff',
                          color: '#374151', fontSize: 13, fontWeight: 600,
                          cursor: editBusy ? 'not-allowed' : 'pointer',
                        }}>취소</button>
                      <button onClick={handleSavePerms} disabled={editBusy}
                        style={{
                          flex: 2, padding: '10px', borderRadius: 6, border: 'none',
                          background: editBusy ? '#9ca3af' : '#EA580C',
                          color: '#fff', fontSize: 13, fontWeight: 700,
                          cursor: editBusy ? 'wait' : 'pointer',
                        }}>{editBusy ? '저장 중...' : '권한 저장'}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
        )}

        {/* 전체 백업 다운로드 ~ 중복 정리 — 사장만 */}
        {!isManagerMode && (<>
        <Card title="💾 전체 데이터 백업 (엑셀)">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
            거래장부·단가표·레시피·직원·스케줄·손익·발주 내역까지 <strong>1개 엑셀 파일</strong>로 저장합니다.
            데이터 손실 사고 대비, <strong>주 1회 다운로드</strong>해서 컴퓨터에 보관해두세요.
          </p>
          <p style={{ fontSize: 11, color: '#9A3412', background: "#FAFAFA",
                       padding: '8px 10px', borderRadius: 6, margin: '0 0 12px', lineHeight: 1.5 }}>
            ✓ 10개 시트 (거래장부 / 단가표 / 레시피 / 직원 / 스케줄 / 손익수기 / 매입메모 / 발주 / 재고 / 설정)<br/>
            ✓ 백업 일시·통계도 함께 저장
          </p>
          <button onClick={handleFullBackup}
            disabled={backupBusy}
            style={{
              padding: '12px 20px', borderRadius: 10,
              border: 'none', background: '#EA580C',
              color: '#fff', fontSize: 14, fontWeight: 800,
              cursor: backupBusy ? 'wait' : 'pointer',
              opacity: backupBusy ? 0.6 : 1,
              boxShadow: '0 3px 12px rgba(30,107,60,0.25)',
            }}>
            {backupBusy ? '⏳ 준비 중...' : '📥 전체 백업 다운로드 (xlsx)'}
          </button>
          {backupResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {backupResult}
            </div>
          )}
        </Card>


        {/* 백업 엑셀 복원 */}
        <Card title="📤 백업 엑셀 복원">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 8px' }}>
            전체 백업으로 받은 <strong>셈핏_백업_*.xlsx</strong> 파일을 올려서 데이터를 복구합니다.
          </p>
          <p style={{ fontSize: 12, color: '#9a3412', background: '#fafafa',
                       padding: '10px 12px', borderRadius: 6, margin: '0 0 12px', lineHeight: 1.6, border: '1px solid #e4e4e7' }}>
            ⚠️ <strong>덮어쓰기 모드</strong>: 현재 데이터가 모두 지워지고 엑셀 파일 내용으로 교체됩니다.<br/>
            ⚠️ 백업 일시 이후 추가된 자료는 사라질 수 있어요. 진행 전 자동으로 스냅샷이 만들어집니다.
          </p>

          {!restoreParsed && (
            <label style={{
              display: 'inline-block', padding: '10px 16px', borderRadius: 8,
              border: '1px dashed #9ca3af', background: '#fafafa',
              fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer',
            }}>
              📁 엑셀 파일 선택
              <input type="file" accept=".xlsx,.xls" onChange={handleRestoreFile}
                     style={{ display: 'none' }} disabled={restoreBusy} />
            </label>
          )}

          {restoreParsed && (() => {
            const s = summarizeParsed(restoreParsed);
            return (
              <div style={{
                padding: 12, background: '#f9fafb', borderRadius: 8,
                border: '1px solid #e5e7eb', marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                  📄 <strong>{restoreFileName}</strong>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
                  백업 일시: <strong>{s.backupDate}</strong>
                </div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                  · 거래장부 {s.transactions}건 · 매입메모 {s.purchaseLog}건<br/>
                  · 단가표 {s.prices}개 · 레시피 {s.recipes}개 · 직원 {s.employees}명<br/>
                  · 스케줄 {s.schedules}개월 · 손익수기 {s.profitLossMonths}개월<br/>
                  · 발주 {s.orderHistory}건 · 재고 {s.inventoryItems}개 품목
                </div>
                {(s.purchaseLog > 0 || s.orderHistory > 0) && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px',
                    background: '#fef2f2', border: '1px solid #E4E4E7',
                    borderRadius: 6, fontSize: 11, color: '#52525B', lineHeight: 1.5,
                  }}>
                    ⚠️ <strong>품목 디테일 손실 주의</strong>: 매입메모·발주내역의 <strong>품목 상세(이름·수량·단가)</strong>는 백업 엑셀에 저장되지 않습니다.
                    복원 시 총액·날짜·거래처만 살아납니다.
                  </div>
                )}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#52525B', marginBottom: 6 }}>
                    확인: <strong>복원</strong>을 입력하세요
                  </div>
                  <input value={restoreConfirmText}
                         onChange={e => setRestoreConfirmText(e.target.value)}
                         placeholder="복원"
                         disabled={restoreBusy}
                         style={{
                           width: '100%', padding: '8px 10px',
                           border: '1px solid #d1d5db', borderRadius: 6,
                           fontSize: 13, marginBottom: 10, boxSizing: 'border-box',
                         }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleConfirmRestore}
                            disabled={restoreBusy || restoreConfirmText !== '복원'}
                            style={{
                              flex: 1, padding: '10px 14px', borderRadius: 8,
                              border: 'none',
                              background: (restoreConfirmText === '복원' && !restoreBusy) ? '#52525B' : '#9ca3af',
                              color: '#fff', fontSize: 13, fontWeight: 700,
                              cursor: (restoreConfirmText === '복원' && !restoreBusy) ? 'pointer' : 'not-allowed',
                            }}>
                      {restoreBusy ? '⏳ 복원 중...' : '🔄 복원 실행'}
                    </button>
                    <button onClick={handleCancelRestore} disabled={restoreBusy}
                            style={{
                              padding: '10px 14px', borderRadius: 8,
                              border: '1px solid #d1d5db', background: '#fff',
                              color: '#374151', fontSize: 13, fontWeight: 600,
                              cursor: restoreBusy ? 'wait' : 'pointer',
                            }}>
                      취소
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {restoreResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {restoreResult}
            </div>
          )}
        </Card>

        {/* 자동 스냅샷 목록 */}
        <Card title="🕒 자동 백업 스냅샷">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
            POS 정리·OCR 중복 정리 같은 <strong>위험 작업 직전</strong>에 자동으로 만들어진 백업 이력입니다.
            데이터 손실 시 셈핏 고객지원에 스냅샷 ID를 알려주면 복원 가능.
          </p>
          {snapLoading ? (
            <div style={{ fontSize: 12, color: '#9ca3af' }}>불러오는 중...</div>
          ) : snapshots.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
              스냅샷 없음 — 위험 작업이 한 번도 실행되지 않았어요
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
              {snapshots.map(s => (
                <div key={s.id} style={{
                  padding: '8px 10px', background: '#f9fafb', borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                  fontSize: 12,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#374151', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 10, color: '#6b7280' }}>
                      {s.createdAtMs ? new Date(s.createdAtMs).toLocaleString('ko-KR') : '?'}
                      {' · '}{s.totalDocs}건 · {s.sizeKB}KB
                      {s.truncated && <span style={{ color: '#52525B' }}> · ⚠ 잘림</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>ID: {s.id}</div>
                  </div>
                  <button onClick={() => handleDeleteSnapshot(s.id)}
                    style={{
                      padding: '4px 8px', borderRadius: 4, border: '1px solid #fca5a5',
                      background: '#fff', color: '#52525B', fontSize: 10, fontWeight: 700,
                      cursor: 'pointer', flexShrink: 0,
                    }}>삭제</button>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: 11, color: '#52525B', background: '#FAFAFA',
                       padding: '8px 10px', borderRadius: 6, marginTop: 10, lineHeight: 1.5 }}>
            ℹ 스냅샷은 30일 후 자동 삭제됩니다. 중요한 시점이면 위 <strong>📥 전체 백업 다운로드</strong>로 컴퓨터에 따로 보관하세요.
          </p>
        </Card>

        {/* POS 데이터 정리 */}
        <Card title="📊 POS 가져오기 데이터 정리">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
            POS 엑셀 가져오기로 등록된 매출 거래만 일괄 삭제합니다.
            중복 임포트로 매출이 부풀려진 경우 정리한 후 다시 가져오면 깔끔해져요.
          </p>
          <p style={{ fontSize: 11, color: '#52525B', background: '#FAFAFA',
                       padding: '8px 10px', borderRadius: 6, margin: '0 0 12px' }}>
            ⚠️ 직접 입력한 매출(빠른 매출 입력)은 영향 없음. POS 임포트 거래(source=pos-import)만 삭제.
          </p>
          <button onClick={handleDeletePosImport}
            disabled={posDeleting}
            style={{
              padding: '10px 18px', borderRadius: 8,
              border: '1.5px solid #B55041', background: '#fff',
              color: '#B55041', fontSize: 13, fontWeight: 600,
              cursor: posDeleting ? 'wait' : 'pointer',
              opacity: posDeleting ? 0.6 : 1,
            }}>
            {posDeleting ? '삭제 중...' : '🗑️ POS 임포트 데이터 모두 삭제'}
          </button>
          {posResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#374151' }}>
              {posResult}
            </div>
          )}
        </Card>

        {/* POS 매출 중복 정리 (정밀) */}
        <Card title="🔁 POS 매출 중복만 정리">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
            POS 엑셀을 여러 번 가져와서 같은 날짜·결제수단·금액 매출이 중복으로 쌓인 경우 정리합니다.
            <strong>각 그룹의 첫 1건만 남기고</strong> 나머지만 삭제 (전체 삭제 X).
          </p>
          <p style={{ fontSize: 11, color: '#9A3412', background: "#FAFAFA",
                       padding: '8px 10px', borderRadius: 6, margin: '0 0 12px' }}>
            ✓ source=pos-import만 검사 — 수기 입력 매출(빠른 매출 입력)은 건드리지 않아요.
          </p>
          <button onClick={handleCleanupPosDuplicates}
            disabled={posDedupAnalyzing}
            style={{
              padding: '10px 18px', borderRadius: 8,
              border: '1.5px solid #FF6F0F', background: '#fff',
              color: '#18181B', fontSize: 13, fontWeight: 600,
              cursor: posDedupAnalyzing ? 'wait' : 'pointer',
              opacity: posDedupAnalyzing ? 0.6 : 1,
            }}>
            {posDedupAnalyzing ? '분석 중...' : '🔍 POS 중복 매출 찾기 + 정리'}
          </button>
          {posDedupResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap' }}>
              {posDedupResult}
            </div>
          )}
        </Card>

        {/* OCR 영수증 중복 정리 */}
        <Card title="🧾 OCR 영수증 중복 정리">
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 12px' }}>
            같은 영수증을 여러 번 OCR로 올린 경우 중복 거래가 쌓여 있을 수 있어요.
            <strong>거래일 + 거래처 + 총액</strong>이 동일한 거래를 찾아서 첫 1건만 남기고 정리합니다.
          </p>
          <p style={{ fontSize: 11, color: '#52525B', background: '#FAFAFA',
                       padding: '8px 10px', borderRadius: 6, margin: '0 0 12px' }}>
            ⚠️ 거래장부 + 메모 영수증 둘 다 검사. 수기 입력(빠른 비용 입력)이라도 같은 fingerprint면 함께 분석됩니다.
          </p>
          <button onClick={handleCleanupOcrDuplicates}
            disabled={dupAnalyzing}
            style={{
              padding: '10px 18px', borderRadius: 8,
              border: '1.5px solid #FF6F0F', background: '#fff',
              color: '#18181B', fontSize: 13, fontWeight: 600,
              cursor: dupAnalyzing ? 'wait' : 'pointer',
              opacity: dupAnalyzing ? 0.6 : 1,
            }}>
            {dupAnalyzing ? '분석 중...' : '🔍 중복 영수증 찾기 + 정리'}
          </button>
          {dupResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap' }}>
              {dupResult}
            </div>
          )}
        </Card>
        </>)}

        {/* 데이터 위치 안내 — 모두 표시 (정보성) */}
        <Card title={Z('🔒 데이터 보관', '🔒 数据保管')}>
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, margin: '0 0 8px 0' }}>
            {lang === 'zh' ? <>门店的所有数据都保存在 <strong>Google Cloud Firebase</strong> 里老板专用的位置。按安全规则，其他用户不能访问，SEMFIT 运营方也不能随意查看。</> : <>
            모든 매장 데이터는 <strong>Google Cloud Firebase</strong>의 사장님 전용 폴더에 보관됩니다.
            보안 규칙로 다른 사용자는 접근할 수 없으며, 셈핏 운영진도 임의 조회할 수 없습니다.</>}
          </p>
          <ul style={{ fontSize: 12, color: '#6b7280', paddingLeft: 18, margin: '4px 0' }}>
            <li>{Z('거래장부·매입·매출 → Firestore ', '账本 · 进货 · 营业额 → Firestore ')}<code>users/{user?.uid?.slice(0,8)}.../</code></li>
            <li>{Z('영수증 사진 → Storage ', '小票照片 → Storage ')}<code>users/{user?.uid?.slice(0,8)}.../receipts/</code></li>
            <li>{Z('모든 통신은 HTTPS(TLS 1.2+) 암호화', '所有通信都用 HTTPS（TLS 1.2+）加密')}</li>
            <li>{Z('저장된 데이터는 Google Cloud KMS로 at-rest 암호화', '保存的数据用 Google Cloud KMS 加密')}</li>
          </ul>
        </Card>

        {/* 약관 */}
        <Card title={Z('📋 약관 및 정책', '📋 条款和政策')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <LinkButton onClick={() => setPrivacyOpen(true)}>
              {Z('🔒 개인정보 처리방침 보기', '🔒 查看隐私政策（韩文）')}
            </LinkButton>
            <LinkButton onClick={() => setSecurityOpen(true)}>
              {Z('🛡️ 보안 정책 보기', '🛡️ 查看安全政策（韩文）')}
            </LinkButton>
          </div>
        </Card>

        {/* 위험 영역 — 회원 탈퇴 (사장만) */}
        {!isManagerMode && (
        <Card title="⚠️ 위험 영역" danger>
          <p style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.7, margin: '0 0 12px 0' }}>
            회원 탈퇴 시 <strong>거래장부·영수증·매장 정보·메뉴·인건비 등 모든 데이터가 즉시 영구 삭제</strong>되며 복구할 수 없습니다.
            세무 신고용으로 보관 중인 영수증 사진도 함께 삭제되니, 필요하면 미리 다운로드하세요.
          </p>

          {step === 0 ? (
            <button onClick={() => setStep(1)} style={{
              padding: '10px 18px', borderRadius: 8,
              border: '1px solid #52525B', background: '#fff',
              color: '#52525B', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}>
              회원 탈퇴 진행
            </button>
          ) : (
            <div style={{
              padding: 14, background: '#fef2f2',
              borderRadius: 8, border: '1px solid #fca5a5',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#52525B', marginBottom: 8 }}>
                정말 탈퇴하시려면 아래에 <strong>"{expectedConfirm}"</strong>을(를) 정확히 입력하세요.
              </div>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={expectedConfirm}
                disabled={deleting}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '1.5px solid #fca5a5', borderRadius: 6,
                  fontSize: 13, marginBottom: 10, boxSizing: 'border-box',
                }} />
              {deleting && (
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
                  ⏳ {progress || '삭제 진행 중...'}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setStep(0); setConfirmText(''); }}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 6,
                    border: '1px solid #ddd', background: '#fff',
                    color: '#555', fontSize: 13, fontWeight: 600,
                    cursor: deleting ? 'not-allowed' : 'pointer',
                  }}>취소</button>
                <button onClick={handleDeleteAccount}
                  disabled={!canDelete}
                  style={{
                    flex: 2, padding: '10px', borderRadius: 6, border: 'none',
                    background: canDelete ? '#52525B' : '#9ca3af',
                    color: '#fff', fontSize: 13, fontWeight: 800,
                    cursor: canDelete ? 'pointer' : 'not-allowed',
                  }}>
                  {deleting ? '삭제 중...' : '🗑 영구 삭제 (되돌릴 수 없음)'}
                </button>
              </div>
            </div>
          )}
        </Card>
        )}

        {/* 푸터 */}
        <div style={{
          marginTop: 30, padding: '14px 0', textAlign: 'center',
          fontSize: 11, color: '#9ca3af', borderTop: '1px solid #e5e7eb',
        }}>
          {Z('문의: ', '联系：')}u012me@gmail.com<br/>
          {Z('© 2026 셈핏 (Semfit) · 매장 운영 OS', '© 2026 SEMFIT · 门店运营系统')}
        </div>
      </div>

      {/* V147: 중국어 화면 — 본문은 한국어 원문이라고 알려 준다 (V144 로그인 약관과 같은 문구) */}
      <PrivacyPolicyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)}
        notice={lang === 'zh' ? '以下内容为韩文原文，以韩文为准。如有疑问，请咨询老板。' : null} okLabel={lang === 'zh' ? '确定' : undefined} />
      <SecurityPolicyModal isOpen={securityOpen} onClose={() => setSecurityOpen(false)}
        notice={lang === 'zh' ? '以下内容为韩文原文，以韩文为准。如有疑问，请咨询老板。' : null} okLabel={lang === 'zh' ? '确定' : undefined} />
    </div>
  );
}

// Storage 폴더 재귀 삭제
async function deleteAllInFolder(folderRef) {
  const result = await listAll(folderRef);
  for (const item of result.items) {
    await deleteObject(item);
  }
  for (const prefix of result.prefixes) {
    await deleteAllInFolder(prefix);
  }
}

function Card({ title, children, danger }) {
  // V62: 미니멀 — 보더 제거, 화이트 카드 위 회색 페이지가 자연스럽게 구분
  return (
    <div style={{
      background: '#fff',
      borderRadius: 14, padding: '22px 24px', marginBottom: 12,
      border: danger ? '1px solid #E4E4E7' : 'none',
    }}>
      <h2 style={{
        fontSize: 16, fontWeight: 800, margin: '0 0 16px 0',
        color: danger ? '#52525B' : '#18181B',
        letterSpacing: '-0.01em',
      }}>{title}</h2>
      {children}
    </div>
  );
}


function LinkButton({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '12px 16px', textAlign: 'left',
      border: '1px solid #F4F4F5', borderRadius: 10,
      background: '#fff', color: '#18181B',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      transition: 'all 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#FF6F0F'; }}
    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E4E4E7'; }}>
      {children}
    </button>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: '1px solid #f3f4f6',
    }}>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
      <span style={{
        fontSize: 13, fontWeight: 600, color: '#1f2937',
        fontFamily: mono ? 'monospace' : 'inherit',
      }}>{value}</span>
    </div>
  );
}

// ── V50: 매니저 권한 편집 컴포넌트 ──
// 카테고리 9개 × 3단계(없음/읽기/입력) + 빠른 프리셋 4종 버튼
function PermissionEditor({ value, onChange }) {
  const norm = normalizePermissions(value);
  const setCat = (catKey, level) => onChange({ ...norm, [catKey]: level });
  const applyPreset = (presetKey) => onChange({ ...PERMISSION_PRESETS[presetKey].permissions });
  const currentPreset = detectPreset(norm);

  return (
    <div>
      {/* 빠른 프리셋 */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>
        빠른 프리셋
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.entries(PERMISSION_PRESETS).map(([key, p]) => {
          const active = currentPreset === key;
          return (
            <button key={key} type="button" onClick={() => applyPreset(key)}
              style={{
                flex: 1, minWidth: 100, padding: '8px 6px',
                borderRadius: 6,
                border: active ? '1.5px solid #FF6F0F' : '1px solid #e5e7eb',
                background: active ? '#FAFAFA' : '#fff',
                color: active ? '#9A3412' : '#374151',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                lineHeight: 1.4,
              }}>
              <div>{p.icon} {p.label}</div>
              <div style={{ fontSize: 9, fontWeight: 500, color: '#9ca3af', marginTop: 2 }}>
                {p.desc}
              </div>
            </button>
          );
        })}
      </div>

      {/* 카테고리별 토글 */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>
        카테고리별 권한 {currentPreset === 'custom' && <span style={{ color: '#18181B' }}>(커스텀)</span>}
      </div>
      <div style={{
        border: '1px solid #e5e7eb', borderRadius: 6,
        overflow: 'hidden',
      }}>
        {PERMISSION_CATEGORIES.map((cat, idx) => {
          const level = norm[cat.key] || 'none';
          return (
            <div key={cat.key} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px',
              background: idx % 2 === 0 ? '#fff' : '#fafafa',
              borderBottom: idx < PERMISSION_CATEGORIES.length - 1 ? '1px solid #f3f4f6' : 'none',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                  {cat.icon} {cat.label}
                </div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>{cat.desc}</div>
              </div>
              <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                {PERMISSION_LEVELS.map(lv => {
                  const c = LEVEL_COLOR[lv];
                  const active = level === lv;
                  return (
                    <button key={lv} type="button" onClick={() => setCat(cat.key, lv)}
                      style={{
                        padding: '5px 9px', borderRadius: 4,
                        border: active ? `1.5px solid ${c.border}` : '1px solid #e5e7eb',
                        background: active ? c.bg : '#fff',
                        color: active ? c.fg : '#9ca3af',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      }}>
                      {LEVEL_LABEL[lv]}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
